import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { MsalProvider } from '@azure/msal-react';

import App from './app/App';
import { msalInstance } from './services/auth/msalConfig';

/**
 * MSAL's silent token renewal (used once the 24h SPA refresh token expires) loads the
 * redirect URI in a hidden iframe and reads the auth response from its URL. Booting the
 * full app there (store, Datadog, API calls, router) slows the frame down and can
 * interfere with the response, causing `monitor_window_timeout`. Leave the frame idle so
 * the parent window can read the response.
 */
function isMsalHiddenFrame(): boolean {
  let inFrame: boolean;
  try {
    inFrame = window.self !== window.top;
  } catch {
    inFrame = true;
  }
  if (!inFrame) return false;
  const params = `${window.location.hash}&${window.location.search}`;
  return /[#?&]state=/.test(params) && /[#?&](code|error)=/.test(params);
}

async function bootstrap() {
  // Initialise MSAL and complete any in-flight redirect before React renders.
  await msalInstance.initialize();
  await msalInstance.handleRedirectPromise();

  // Note: StrictMode is intentionally omitted — its dev-only double-invoke of
  // mount effects / componentDidMount causes the legacy fetch-on-mount screens
  // to fire every API request twice in development.
  createRoot(document.getElementById('root')!).render(
    <MsalProvider instance={msalInstance}>
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </MsalProvider>,
  );
}

if (!isMsalHiddenFrame()) {
  bootstrap();
}
